import React from "react";
import { Link } from "react-router-dom";

const caseStudies = [
  {
    industry: "Retail & E-commerce",
    category: "Case Study",
    title: "DTC brand consolidated monthly close into a single, structured workflow",
    metric: "From scattered spreadsheets to one repeatable process",
    challenge: "Finance reporting was split across multiple spreadsheets with no shared framework for categorization or forecasting.",
    approach: "Implemented Budget Planner modes (zero-based, subscriptions, forecast) with household-style role permissions for team collaboration.",
    outcome: "Monthly close now runs through a single structured workflow with consistent categorization and fewer reconciliation back-and-forths.",
  },
  {
    industry: "Marketing Agency",
    category: "Case Study",
    title: "Agency standardized proposal delivery and made it delegatable",
    metric: "Proposals went from ad-hoc to systemized",
    challenge: "Proposal prep required manual copy drafting, design consistency checks, and repetitive financial projections with no standard template.",
    approach: "Combined copy, design, and financial tooling into a single workflow with pre-approved templates and structured scoping steps.",
    outcome: "Team delivered faster, more consistent proposals while reducing context switching across multiple separate SaaS tools.",
  },
  {
    industry: "Financial Advisory",
    category: "Case Study",
    title: "Advisor systematized client portfolio review preparation",
    metric: "Pre-meeting prep became a defined, repeatable workflow",
    challenge: "Advisors spent too much time doing ad-hoc scenario analysis before each client meeting with no consistent structure.",
    approach: "Configured Portfolio Risk Analyzer with reusable client profile presets and a standardized reporting output format.",
    outcome: "Advisors arrived at meetings with consistent, structured analysis instead of improvised research — increasing confidence and credibility.",
  },
  {
    industry: "Professional Services",
    category: "Framework",
    title: "Operational reporting aligned across departments in one engagement",
    metric: "Single source of truth across teams",
    challenge: "Department-level reports used inconsistent definitions and metrics, creating leadership confusion and duplicated effort.",
    approach: "Ran an Assess → Build engagement to define KPI taxonomy and align tooling around shared definitions and reporting cadences.",
    outcome: "Executive reporting became consistent and decision-ready with fewer back-and-forth clarification cycles before each leadership review.",
  },
];

export default function Outcomes() {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-indigo-300 font-semibold uppercase tracking-widest text-sm mb-5">Outcomes</p>
          <h1 className="text-5xl font-extrabold mb-6 leading-tight">
            We measure success in results,
            <br />
            not activity.
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Every engagement includes explicit success criteria and measurable outcomes. These are representative results from real client workflows.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {caseStudies.map((c) => (
              <article key={c.title} className="border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition bg-white">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-bold bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full">{c.category}</span>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{c.industry}</span>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-2 leading-tight">{c.title}</h2>
                <p className="text-sm font-semibold text-emerald-600 mb-5">{c.metric}</p>
                <div className="space-y-3 text-sm">
                  <p className="text-gray-600"><span className="font-semibold text-gray-800">Challenge:</span> {c.challenge}</p>
                  <p className="text-gray-600"><span className="font-semibold text-gray-800">Approach:</span> {c.approach}</p>
                  <p className="text-gray-600"><span className="font-semibold text-gray-800">Outcome:</span> {c.outcome}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-4">Engagement Principles</p>
          <h2 className="text-3xl font-bold text-gray-900 mb-10">How we create repeatable outcomes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              {
                title: "Define success upfront",
                desc: "We agree on measurable KPIs before implementation begins.",
              },
              {
                title: "Ship in short cycles",
                desc: "Weekly milestones reduce risk and make progress visible.",
              },
              {
                title: "Document for durability",
                desc: "Your team receives playbooks and handoff docs, not just a delivered setup.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Want outcomes like these?</h2>
          <p className="text-gray-500 mb-8">Start with a focused 30-minute scoping call. We will map your highest-impact opportunities.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/consulting" className="inline-block bg-indigo-600 text-white font-bold px-8 py-3.5 rounded-xl hover:bg-indigo-700 transition">Talk to Consulting →</Link>
            <Link to="/trust" className="inline-block border border-gray-300 text-gray-700 font-semibold px-8 py-3.5 rounded-xl hover:border-indigo-300 hover:text-indigo-700 transition">Review Trust & Security</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
